/**
 * SummaryTab - Risk Snapshot grid + FOIR Trend chart with threshold markers
 */

import React from 'react';
import type { SummaryTabProps } from '../../../../types/lending-report.types';
import { getMonthLabel } from '../../../../utils/lending-report-utils';

const RISK_DESCRIPTIONS: Record<string, Record<string, string>> = {
  'Income Stability': {
    GOOD: 'Salary credited consistently on expected dates',
    MODERATE: 'Minor variations in salary timing',
    POOR: 'Irregular or missing salary credits',
  },
  'FOIR Risk': {
    GOOD: 'Obligations well within income capacity',
    MODERATE: 'Fixed obligations as a share of income',
    POOR: 'High obligations relative to income',
  },
  'Repayment Discipline': {
    GOOD: 'Minimal delays in EMI repayments',
    MODERATE: 'Occasional payment delays observed',
    POOR: 'Frequent missed or delayed payments',
  },
  'Liquidity': {
    GOOD: 'Sufficient buffer for unexpected expenses',
    MODERATE: 'Adequate balance maintained',
    POOR: 'Low balance or near-zero events detected',
  },
  'Investment Behavior': {
    GOOD: 'Regular investment contributions observed',
    MODERATE: 'Minimal investment activity',
    POOR: 'No investment contributions detected',
  },
};

const STATUS_LABELS: Record<string, string> = {
  GOOD: 'Stable',
  MODERATE: 'Moderate',
  POOR: 'Poor',
};

/**
 * FOIR Trend line chart with 40% and 55% threshold lines
 */
const FoirTrendChart: React.FC<{ data: { month: string; foir: number }[] }> = ({ data }) => {
  if (data.length === 0) {
    return <div className="text-gray-400 text-sm text-center py-8">No FOIR trend data available</div>;
  }

  const width = 560;
  const height = 220;
  const padding = { top: 20, right: 20, bottom: 40, left: 50 };
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;

  const maxFoir = Math.max(...data.map(d => d.foir), 80);
  const range = maxFoir || 1;

  const points = data.map((d, i) => {
    const x = padding.left + (i / Math.max(data.length - 1, 1)) * chartW;
    const y = padding.top + chartH - (d.foir / range) * chartH;
    return { x, y, ...d };
  });

  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

  const thresholdY = (val: number) => padding.top + chartH - (val / range) * chartH;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ maxHeight: 220 }}>
      {/* Grid lines */}
      {[0, 20, 40, 60, 80].filter(v => v <= maxFoir).map(v => {
        const y = thresholdY(v);
        return (
          <g key={v}>
            <line x1={padding.left} y1={y} x2={width - padding.right} y2={y} stroke="#f0f0f0" strokeWidth={1} />
            <text x={padding.left - 8} y={y + 4} textAnchor="end" fill="#9ca3af" fontSize={10}>{v}</text>
          </g>
        );
      })}

      {/* 40% threshold line (amber) */}
      <line
        x1={padding.left} y1={thresholdY(40)}
        x2={width - padding.right} y2={thresholdY(40)}
        stroke="#f59e0b" strokeWidth={1.5} strokeDasharray="6 4"
      />
      <text x={width - padding.right + 4} y={thresholdY(40) + 4} fill="#f59e0b" fontSize={9} fontWeight={600}>40%</text>

      {/* 50% threshold line (red) */}
      <line
        x1={padding.left} y1={thresholdY(50)}
        x2={width - padding.right} y2={thresholdY(50)}
        stroke="#ef4444" strokeWidth={1.5} strokeDasharray="6 4"
      />
      <text x={width - padding.right + 4} y={thresholdY(50) + 4} fill="#ef4444" fontSize={9} fontWeight={600}>50%</text>

      {/* Line */}
      <path d={pathD} fill="none" stroke="#3b82f6" strokeWidth={2} />

      {/* Dots */}
      {points.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r={4} fill="white" stroke="#3b82f6" strokeWidth={2} />
      ))}

      {/* X labels */}
      {points.map((p, i) => (
        <text key={i} x={p.x} y={height - 8} textAnchor="middle" fill="#9ca3af" fontSize={9}>
          {getMonthLabel(p.month)}
        </text>
      ))}
    </svg>
  );
};

export const SummaryTab: React.FC<SummaryTabProps> = ({ report }) => {
  return (
    <div className="space-y-6">
      {/* Risk Snapshot */}
      <div>
        <h3 style={{ fontSize: '14px', fontWeight: 700, color: '#111827', marginBottom: '12px' }}>Risk Snapshot</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
          {report.riskSnapshot.map((item, i) => {
            const statusColors: Record<string, { bg: string; text: string }> = {
              GOOD: { bg: '#ecfdf5', text: '#059669' },
              MODERATE: { bg: '#fffbeb', text: '#d97706' },
              POOR: { bg: '#fef2f2', text: '#dc2626' },
            };
            const colors = statusColors[item.status] || statusColors.MODERATE;
            const description = RISK_DESCRIPTIONS[item.label]?.[item.status] || item.tag || '';
            const statusLabel = STATUS_LABELS[item.status] || item.status;

            return (
              <div
                key={i}
                style={{
                  padding: '16px',
                  borderRadius: '12px',
                  backgroundColor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                }}
              >
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>{item.label}</div>
                <div style={{
                  display: 'inline-block',
                  padding: '2px 10px',
                  borderRadius: '9999px',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: colors.text,
                  backgroundColor: colors.bg,
                  marginBottom: '8px',
                }}>
                  {statusLabel}
                </div>
                <div style={{ fontSize: '11px', color: '#9ca3af', lineHeight: 1.4 }}>{description}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* FOIR Trend */}
      <div>
        <h3 style={{ fontSize: '14px', fontWeight: 700, color: '#111827', marginBottom: '12px' }}>FOIR Trend (12 Months)</h3>
        <div className="p-4 rounded-xl bg-white border border-gray-100 shadow-sm">
          <FoirTrendChart data={report.foirTrend} />
        </div>
      </div>
    </div>
  );
};
